/**
 * URS-F488 AgendaEfficiencyPanel
 *
 * Pestana "Eficiencia de Agenda" dentro de Estadisticas. Muestra:
 * - Filtros: rango de fechas (default: -30d ... +60d) + estudio opcional
 * - 4 KPI cards: total slots, ocupados, reservados sin cita, libres
 * - Ratio de eficiencia y "waste"
 * - Serie diaria (barras apiladas ocupado/reservado/libre)
 * - Tabla por estudio con % de cumplimiento
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { CalendarBlank, Warning, CheckCircle, Circle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function isoDaysFromToday(delta) {
  const d = new Date();
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}

export default function AgendaEfficiencyPanel({ lang = 'es' }) {
  const [dateFrom, setDateFrom] = useState(isoDaysFromToday(-30));
  const [dateTo, setDateTo] = useState(isoDaysFromToday(60));
  const [studyId, setStudyId] = useState('');
  const [studies, setStudies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/studies?limit=2000`, { withCredentials: true })
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || data.data || []);
        setStudies(items.map(s => ({ value: s.study_id, label: `${s.code || s.study_id} — ${s.title || ''}`.trim() })));
      })
      .catch(() => setStudies([]));
  }, []);

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (studyId) {
        params.set('scope', 'by_study');
        params.set('study_id', studyId);
      }
      const { data } = await axios.get(`${API_URL}/api/statistics/agenda-efficiency?${params}`, { withCredentials: true });
      setData(data);
    } catch (err) {
      toast.error(lang === 'es' ? 'Error al cargar eficiencia' : 'Error loading efficiency');
    }
    setLoading(false);
  }, [dateFrom, dateTo, studyId, lang]);

  useEffect(() => { load(); }, [load]);

  const workingDays = useMemo(() => (data?.by_day || []).filter(d => d.working), [data]);

  // URS-F488b: motor interno usa buckets de 5 min (para cuadrar 1:1 con la
  // vista Dia de la agenda). En la UI presentamos "huecos equivalentes"
  // segun `presentation_slot_minutes` (15 min por defecto) + minutos absolutos.
  const bucket = data?.slot_minutes || 5;
  const presMin = data?.presentation_slot_minutes || 15;
  const groupFactor = Math.max(1, Math.round(presMin / bucket)); // 3 si 5→15

  const fmtHM = (mins) => {
    const m = Math.max(0, Math.round(mins));
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h === 0) return `${r}min`;
    if (r === 0) return `${h}h`;
    return `${h}h ${r}min`;
  };
  const fmtCountDual = (slotCount) => {
    const mins = slotCount * bucket;
    const equiv = Math.round((slotCount / groupFactor) * 10) / 10;
    return { minutes: mins, minutesText: fmtHM(mins), equivHuecos: equiv };
  };

  // Barras diarias: agrupamos slots de 5 min en tramos de `groupFactor`
  // (por defecto 3 → 15 min). El estado del tramo agregado es el del
  // MAYOR peso: si tiene 1 o mas "occupied" o "reserved" cuentan; si
  // no, mira "free". Regla de prioridad: occupied > reserved > free.
  const groupedDays = useMemo(() => {
    return workingDays.map(d => ({
      date: d.date,
      occupied_min: (d.occupied || 0) * bucket,
      reserved_no_appt_min: (d.reserved_no_appt || 0) * bucket,
      free_min: (d.free || 0) * bucket,
      blocked_min: (d.blocked || 0) * bucket,
      occ_h: (d.occupied || 0) / groupFactor,
      res_h: (d.reserved_no_appt || 0) / groupFactor,
      free_h: (d.free || 0) / groupFactor,
    }));
  }, [workingDays, bucket, groupFactor]);

  const maxDayEquiv = useMemo(() => {
    let m = 0;
    for (const d of groupedDays) {
      const t = d.occ_h + d.res_h + d.free_h;
      if (t > m) m = t;
    }
    return m || 1;
  }, [groupedDays]);

  const lb = lang === 'es' ? {
    date_from: 'Desde',
    date_to: 'Hasta',
    study: 'Estudio',
    all: 'Todos los estudios',
    apply: 'Aplicar',
    kpi_total: 'Total huecos laborables',
    kpi_occupied: 'Ocupados',
    kpi_reserved: 'Reservados sin cita',
    kpi_free: 'Totalmente libres',
    kpi_blocked: 'Bloqueados',
    efficiency: 'Eficiencia',
    waste: 'Desperdicio reservado',
    subtitle: 'Cruce de huecos laborables con programación por estudio y citas agendadas',
    per_study: 'Cumplimiento por estudio',
    col_code: 'Estudio', col_title: 'Título', col_reserved: 'Reservados',
    col_occupied: 'Con cita', col_wasted: 'Sin cita', col_compliance: '% cumplimiento',
    no_reserved: 'Ningún estudio ha reservado huecos en el rango.',
    hint: 'Motor de cálculo con precisión de 5 min (coincide con la vista Día de la agenda). Los KPIs muestran duración total y equivalente en huecos de 15 min. Días no laborables y festivos no cuentan.',
    daily_title: 'Distribución diaria',
    daily_hint: 'Cada barra suma los huecos laborables del día',
  } : {
    date_from: 'From', date_to: 'To', study: 'Study', all: 'All studies', apply: 'Apply',
    kpi_total: 'Total working slots', kpi_occupied: 'Occupied',
    kpi_reserved: 'Reserved w/o appointment', kpi_free: 'Fully free',
    kpi_blocked: 'Blocked', efficiency: 'Efficiency', waste: 'Reserved waste',
    subtitle: 'Cross of working slots with study programming and scheduled appointments',
    per_study: 'Study compliance',
    col_code: 'Study', col_title: 'Title', col_reserved: 'Reserved',
    col_occupied: 'Filled', col_wasted: 'Empty', col_compliance: '% compliance',
    no_reserved: 'No study reserved slots in this range.',
    hint: 'Compute engine uses 5-min precision (matches agenda Day view). KPIs show total duration and equivalent 15-min slot count. Non-working days and holidays are excluded.',
    daily_title: 'Daily breakdown', daily_hint: 'Each bar sums working slots of the day',
  };

  return (
    <div className="space-y-6" data-testid="agenda-efficiency-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-stone-500 max-w-3xl">{lb.subtitle}. <span className="text-stone-400">{lb.hint}</span></p>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 bg-stone-50 border border-stone-200 rounded-sm p-3">
        <div>
          <Label className="text-xs mb-1">{lb.date_from}</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="eff-date-from" />
        </div>
        <div>
          <Label className="text-xs mb-1">{lb.date_to}</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="eff-date-to" />
        </div>
        <div className="flex-1 min-w-[240px] max-w-md">
          <Label className="text-xs mb-1">{lb.study}</Label>
          <select
            value={studyId}
            onChange={e => setStudyId(e.target.value)}
            className="w-full h-9 px-2 border border-stone-200 rounded-sm bg-white text-sm"
            data-testid="eff-study-select"
          >
            <option value="">{lb.all}</option>
            {studies.map(s => (<option key={s.value} value={s.value}>{s.label}</option>))}
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2" data-testid="eff-apply-btn">
          <CalendarBlank size={14} />{loading ? '…' : lb.apply}
        </Button>
      </div>

      {!data ? (
        <p className="text-sm text-stone-400 text-center py-6">{lang === 'es' ? 'Cargando…' : 'Loading…'}</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {(() => {
              const total = fmtCountDual(data.total_workable_slots);
              const occ = fmtCountDual(data.totals.occupied);
              const res = fmtCountDual(data.totals.reserved_no_appt);
              const free = fmtCountDual(data.totals.free);
              const blocked = fmtCountDual(data.totals.blocked);
              const eqLabel = (n) => `~${n} ${lang === 'es' ? 'huecos' : 'slots'} × ${presMin} min`;
              return (
                <>
                  <KpiCard
                    label={lb.kpi_total}
                    value={total.minutesText}
                    detail={`${eqLabel(total.equivHuecos)} · ${workingDays.length} ${lang === 'es' ? 'días' : 'days'}`}
                    testid="eff-kpi-total"
                  />
                  <KpiCard
                    label={lb.kpi_occupied}
                    value={occ.minutesText}
                    accent="#2f7a3d"
                    icon={<CheckCircle size={16} weight="fill" />}
                    detail={`${eqLabel(occ.equivHuecos)} · ${data.efficiency_pct}% ${lb.efficiency}`}
                    testid="eff-kpi-occupied"
                  />
                  <KpiCard
                    label={lb.kpi_reserved}
                    value={res.minutesText}
                    accent="#c78700"
                    icon={<Warning size={16} weight="fill" />}
                    detail={`${eqLabel(res.equivHuecos)} · ${data.waste_pct}% ${lb.waste}`}
                    testid="eff-kpi-reserved"
                  />
                  <KpiCard
                    label={lb.kpi_free}
                    value={free.minutesText}
                    accent="#5b6473"
                    icon={<Circle size={16} />}
                    detail={eqLabel(free.equivHuecos)}
                    testid="eff-kpi-free"
                  />
                  <KpiCard
                    label={lb.kpi_blocked}
                    value={blocked.minutesText}
                    accent="#5b6473"
                    detail={eqLabel(blocked.equivHuecos)}
                    testid="eff-kpi-blocked"
                  />
                </>
              );
            })()}
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-4">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="text-sm font-medium text-stone-900">{lb.daily_title}</h3>
              <span className="text-[11px] text-stone-400">
                {lang === 'es'
                  ? `Cada barra suma los huecos del día agrupados en tramos de ${presMin} min`
                  : `Each bar sums working slots grouped in ${presMin}-min blocks`}
              </span>
            </div>
            <div className="flex items-end gap-0.5 h-40 overflow-x-auto pb-4 border-b border-stone-100">
              {groupedDays.map(d => {
                const h = (v) => `${Math.round((v / maxDayEquiv) * 100)}%`;
                return (
                  <div
                    key={d.date}
                    className="flex flex-col items-center gap-1 flex-shrink-0"
                    style={{ width: 18 }}
                    title={`${d.date} · ${fmtHM(d.occupied_min + d.reserved_no_appt_min + d.free_min)}`}
                    data-testid={`eff-day-bar-${d.date}`}
                  >
                    <div className="flex flex-col justify-end w-3 h-full">
                      <div style={{ height: h(d.free_h), backgroundColor: '#e7e5e4' }} title={`Free ${fmtHM(d.free_min)}`} />
                      <div style={{ height: h(d.res_h), backgroundColor: '#c78700' }} title={`Reserved no appt ${fmtHM(d.reserved_no_appt_min)}`} />
                      <div style={{ height: h(d.occ_h), backgroundColor: '#2f7a3d' }} title={`Occupied ${fmtHM(d.occupied_min)}`} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-4 mt-3 text-[11px] text-stone-500">
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ backgroundColor: '#2f7a3d' }} />{lb.kpi_occupied}</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ backgroundColor: '#c78700' }} />{lb.kpi_reserved}</span>
              <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3" style={{ backgroundColor: '#e7e5e4' }} />{lb.kpi_free}</span>
            </div>
          </div>

          <div className="bg-white border border-stone-200 rounded-sm p-4">
            <h3 className="text-sm font-medium text-stone-900 mb-3">{lb.per_study}</h3>
            {(data.by_study || []).length === 0 ? (
              <p className="text-sm text-stone-400 text-center py-6">{lb.no_reserved}</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">{lb.col_code}</TableHead>
                    <TableHead>{lb.col_title}</TableHead>
                    <TableHead className="w-28 text-right">{lb.col_reserved}</TableHead>
                    <TableHead className="w-28 text-right">{lb.col_occupied}</TableHead>
                    <TableHead className="w-28 text-right">{lb.col_wasted}</TableHead>
                    <TableHead className="w-36 text-right">{lb.col_compliance}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_study.map(s => {
                    const pct = s.compliance_pct;
                    let color = 'bg-stone-100 text-stone-600 border-stone-200';
                    if (pct != null) {
                      if (pct >= 85) color = 'bg-emerald-50 text-emerald-700 border-emerald-200';
                      else if (pct >= 60) color = 'bg-amber-50 text-amber-700 border-amber-200';
                      else color = 'bg-red-50 text-red-700 border-red-200';
                    }
                    const resD = fmtCountDual(s.reserved_slots);
                    const occD = fmtCountDual(s.occupied_slots);
                    const wasteD = fmtCountDual(s.reserved_no_appt);
                    return (
                      <TableRow key={s.study_id} data-testid={`eff-study-row-${s.study_id}`}>
                        <TableCell className="font-mono text-xs text-stone-700">{s.code}</TableCell>
                        <TableCell className="text-sm text-stone-600 truncate max-w-md">{s.title}</TableCell>
                        <TableCell className="text-right text-sm">
                          <div>{resD.minutesText}</div>
                          <div className="text-[10px] text-stone-400">~{resD.equivHuecos} × {presMin}min</div>
                        </TableCell>
                        <TableCell className="text-right text-sm text-emerald-700">
                          <div>{occD.minutesText}</div>
                          <div className="text-[10px] text-stone-400">~{occD.equivHuecos} × {presMin}min</div>
                        </TableCell>
                        <TableCell className="text-right text-sm text-amber-700">
                          <div>{wasteD.minutesText}</div>
                          <div className="text-[10px] text-stone-400">~{wasteD.equivHuecos} × {presMin}min</div>
                        </TableCell>
                        <TableCell className="text-right">
                          {pct == null ? <span className="text-stone-400 text-xs">—</span> : (
                            <Badge variant="outline" className={`text-xs ${color}`}>{pct}%</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, detail, accent = '#1f3e80', icon = null, testid }) {
  return (
    <div className="bg-white border border-stone-200 rounded-sm p-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">{label}</span>
        {icon && <span style={{ color: accent }}>{icon}</span>}
      </div>
      <div className="text-2xl font-heading font-semibold mt-1" style={{ color: accent }}>{value}</div>
      {detail && <div className="text-[11px] text-stone-500 mt-0.5">{detail}</div>}
    </div>
  );
}
